/**
 * Presentation of domain values. The domain stores weight in grams and money as an amount plus a
 * currency (ADR-005); everything the user reads goes through these helpers so units, decimal
 * separators and time zones follow the tenant's settings rather than the browser's guess.
 */

/** Locale, currency, weight unit and time zone of the current tenant. */
export interface TenantFormatSettings {
  locale: string;
  currency: string;
  weightUnit: WeightUnit;
  timeZone: string;
  /** How much weight a packing run may lose before the API refuses it, in percent (US-04.4). */
  packagingLossTolerancePp: number;
  /** Whether packing is refused when the bags and labels are not in stock. */
  packagingRequiresConsumables: boolean;
}

/** Matches the backend enum; storage is always grams (ADR-005). */
export type WeightUnit = "KG" | "LB";

const GRAMS_PER_KILOGRAM = 1_000;
const GRAMS_PER_POUND = 453.59237;
const GRAMS_PER_OUNCE = GRAMS_PER_POUND / 16;

const weightUnitSymbols: Record<WeightUnit, string> = { KG: "kg", LB: "lb" };

/** What a weight below one unit is written in: grams under a kilogram, ounces under a pound. */
const subUnitSymbols: Record<WeightUnit, string> = { KG: "g", LB: "oz" };

export function weightUnitSymbol(unit: WeightUnit): string {
  return weightUnitSymbols[unit];
}

/** The small unit that goes with this one — for labels on inputs that take either (US-17.4). */
export function weightSubUnitSymbol(unit: WeightUnit): string {
  return subUnitSymbols[unit];
}

export const defaultFormatSettings: TenantFormatSettings = {
  locale: "pl",
  currency: "PLN",
  weightUnit: "KG",
  timeZone: "Europe/Warsaw",
  packagingLossTolerancePp: 1,
  packagingRequiresConsumables: true,
};

/**
 * Formats a weight held in grams. Below one unit the value is shown in the small unit that goes with
 * it — grams under a kilogram, ounces under a pound (US-17.4) — which is how roasters talk about
 * samples and packaging. Nothing at all is the exception: zero reads as "0 kg" in a column of
 * kilograms rather than as the "0 g" the rule would otherwise produce.
 */
export function formatWeight(
  grams: number,
  settings: TenantFormatSettings = defaultFormatSettings,
  options: { unit?: WeightUnit; maximumFractionDigits?: number } = {},
): string {
  const unit = options.unit ?? settings.weightUnit;
  const divisor = unit === "LB" ? GRAMS_PER_POUND : GRAMS_PER_KILOGRAM;

  if (grams !== 0 && Math.abs(grams) < divisor) {
    // Ounces carry one decimal, grams none: a gram is already the smallest thing the domain counts,
    // while "8 oz" instead of "8,8 oz" would lose a quarter of a bag.
    const subUnit = unit === "LB" ? grams / GRAMS_PER_OUNCE : grams;
    const digits = unit === "LB" ? 1 : 0;
    return `${new Intl.NumberFormat(settings.locale, { maximumFractionDigits: digits }).format(subUnit)} ${
      subUnitSymbols[unit]
    }`;
  }
  const value = grams / divisor;
  const formatted = new Intl.NumberFormat(settings.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(value);
  return `${formatted} ${weightUnitSymbol(unit)}`;
}

/**
 * Grams as a number in the tenant's unit, for pre-filling an input field that {@link gramsFromUnit}
 * will convert back. Unrounded on purpose: 12500 g is 12.5 kg, and an edit form must not silently
 * change the stored weight.
 */
export function unitFromGrams(
  grams: number,
  settings: TenantFormatSettings = defaultFormatSettings,
  unit: WeightUnit = settings.weightUnit,
): number {
  return grams / (unit === "LB" ? GRAMS_PER_POUND : GRAMS_PER_KILOGRAM);
}

/**
 * The inverse of {@link formatWeight} for input fields: takes what the user typed in their own unit
 * (kilograms or pounds) and returns the grams the API stores. Rounded, because a gram is the
 * smallest thing the domain counts.
 */
export function gramsFromUnit(
  value: number,
  settings: TenantFormatSettings = defaultFormatSettings,
  unit: WeightUnit = settings.weightUnit,
): number {
  return Math.round(value * (unit === "LB" ? GRAMS_PER_POUND : GRAMS_PER_KILOGRAM));
}

/** Formats an amount in the tenant's currency; pass `currency` to override it (multi-currency pricing). */
export function formatMoney(
  amount: number | string,
  settings: TenantFormatSettings = defaultFormatSettings,
  currency: string = settings.currency,
): string {
  const value = typeof amount === "string" ? Number(amount) : amount;
  return new Intl.NumberFormat(settings.locale, { style: "currency", currency }).format(value);
}

/**
 * Formats a bare number in the tenant's locale. For quantities that carry their own unit in the
 * label — percentage points, pieces — where the currency and percent styles would be wrong but the
 * decimal separator still has to follow the language.
 */
export function formatNumber(
  value: number,
  settings: TenantFormatSettings = defaultFormatSettings,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(settings.locale, options).format(value);
}

/**
 * Reads back a number somebody typed — or that {@link formatNumber} wrote for them (US-37.4).
 *
 * <p>A form field for money is a string until it is submitted, because "14," is not an invalid form
 * yet, it is somebody still typing. What it has to survive on the way back is everything a locale
 * puts into a number: a comma for the decimal point, and — the one that bites — the space Polish and
 * German use between thousands, which `Intl` writes as U+00A0 or U+202F and `Number()` refuses.
 *
 * <p>Returns `null` for anything that is not a number, blank included, so a caller can tell "nothing
 * was typed" from "zero was typed".
 */
export function parseDecimal(text: string): number | null {
  const cleaned = text.replace(/[\s\u00A0\u202F\u2009]/gu, "").replace(",", ".");
  if (cleaned === "") {
    return null;
  }
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/** Formats a percentage that arrives as a plain number (15.25 -> "15,25 %"). */
export function formatPercent(
  percentage: number,
  settings: TenantFormatSettings = defaultFormatSettings,
  maximumFractionDigits = 2,
): string {
  return new Intl.NumberFormat(settings.locale, {
    style: "percent",
    maximumFractionDigits,
  }).format(percentage / 100);
}

/** `2026-09-15`: a day, not a moment. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats an instant in the tenant's time zone — or a business date as the day it is (US-37.6).
 *
 * <p>The two are not the same thing and must not be formatted the same way. `2026-09-15` is the
 * fifteenth in Warsaw and the fifteenth in Portland; it carries no time. Read as an instant it is
 * midnight UTC, and printing midnight UTC in a zone behind UTC gives the fourteenth — a due date
 * that moves a day west, which is the kind of wrong nobody reports because it looks plausible.
 */
export function formatDate(
  value: Date | string | number,
  settings: TenantFormatSettings = defaultFormatSettings,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): string {
  if (typeof value === "string" && CALENDAR_DATE.test(value)) {
    return new Intl.DateTimeFormat(settings.locale, { timeZone: "UTC", ...options }).format(
      new Date(`${value}T00:00:00Z`),
    );
  }
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(settings.locale, { timeZone: settings.timeZone, ...options }).format(date);
}

export function formatDateTime(
  value: Date | string | number,
  settings: TenantFormatSettings = defaultFormatSettings,
): string {
  return formatDate(value, settings, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * A length of time as a roaster says it: `10:40`, and `1:02:30` once it passes an hour (EP-05).
 *
 * Deliberately not localised. Roast times are read off a stopwatch and compared between roasts, and
 * `m:ss` is the notation every roasting tool — Artisan included — prints them in; a locale-aware
 * "10 min 40 s" would be harder to scan and impossible to line up in a column.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) {
    return "-";
  }
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60) % 60;
  const remainder = total % 60;
  const hours = Math.floor(total / 3600);
  const padded = `${String(minutes).padStart(hours > 0 ? 2 : 1, "0")}:${String(remainder).padStart(2, "0")}`;
  return hours > 0 ? `${hours}:${padded}` : padded;
}

/** Steps a relative time is rounded to, largest first; the first one that fits wins. */
const relativeSteps: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
];

/**
 * "5 minut temu" / "5 minutes ago" — how a notification says when it arrived (US-16.3). A list of
 * things that just happened reads better this way than in timestamps, and `Intl.RelativeTimeFormat`
 * declines the number correctly in Polish, which a hand-written string cannot.
 *
 * <p>Anything under a minute is "just now": a notification is at most a queue poll old, and "in 0
 * seconds" would be both wrong and unsettling.
 */
export function formatRelativeTime(
  value: Date | string | number,
  settings: TenantFormatSettings = defaultFormatSettings,
  now: Date = new Date(),
): string {
  const date = value instanceof Date ? value : new Date(value);
  const elapsed = date.getTime() - now.getTime();
  const format = new Intl.RelativeTimeFormat(settings.locale, { numeric: "auto" });

  for (const [unit, milliseconds] of relativeSteps) {
    if (Math.abs(elapsed) >= milliseconds) {
      return format.format(Math.round(elapsed / milliseconds), unit);
    }
  }
  return format.format(0, "second");
}

/**
 * The calendar day an instant falls on in the given zone, as `YYYY-MM-DD` (US-37.6).
 *
 * <p>`en-CA` is used purely because its short date format <em>is</em> ISO.
 */
export function dayIn(value: Date | string | number, timeZone: string): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Today in the roastery's own calendar, as `YYYY-MM-DD`.
 *
 * <p>Not `new Date().toISOString()`: after midnight in Warsaw the UTC date is still yesterday, and a
 * screen that opens on "today" would open on the wrong day for the first two hours of every morning.
 */
export function today(timeZone: string): string {
  return dayIn(new Date(), timeZone);
}

/** The day before or after one of those, without dragging a date library in for it. */
export function shiftDay(date: string, days: number): string {
  const shifted = new Date(`${date}T12:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}
